import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

interface RefreshInfluencerMetricsResult {
  influencerId: string;
  handle?: string;
  status: 'success' | 'skipped' | 'failed';
  error?: string;
}

interface RefreshInfluencerMetricsResponse {
  error?: string;
  message?: string;
  results?: RefreshInfluencerMetricsResult[];
}

export function useEnqueueEnrichment() {
  const queryClient = useQueryClient();
  const [isEnqueueing, setIsEnqueueing] = useState(false);

  const enqueueJob = async (influencerIds: string[], handles: string[]) => {
    if (!influencerIds.length || !handles.length || influencerIds.length !== handles.length) {
      console.error('Invalid arguments for enqueueJob');
      return;
    }

    setIsEnqueueing(true);
    try {
      const { data, error } = await supabase.functions.invoke<RefreshInfluencerMetricsResponse>('refresh-influencer-metrics', {
        body: { influencerIds },
      });

      if (error) {
        throw new Error(data?.error || error.message);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const results = data?.results ?? [];
      const failedResults = results.filter((result) => result.status === 'failed');
      const skippedResults = results.filter((result) => result.status === 'skipped');

      queryClient.invalidateQueries({ queryKey: ['influencers'] });

      if (failedResults.length > 0) {
        const firstHandle = failedResults[0].handle;
        throw new Error(
          failedResults.length === 1
            ? `We couldn't refresh metrics for @${firstHandle ?? 'this influencer'}. Please try again later.`
            : `${failedResults.length} influencers couldn't be refreshed. Please try again later.`
        );
      }

      toast({
        title: skippedResults.length > 0 ? "Metrics Refresh Finished" : "Metrics Refreshed",
        description: skippedResults.length > 0
          ? `No recent posts were available for ${skippedResults.length} ${skippedResults.length === 1 ? 'influencer' : 'influencers'}. Their existing metrics were left unchanged.`
          : `Successfully refreshed metrics for ${handles.length} ${handles.length === 1 ? 'influencer' : 'influencers'}.`,
      });
      
      return data;
    } catch (err: unknown) {
      console.error('Error refreshing metrics:', err);
      throw err;
    } finally {
      setIsEnqueueing(false);
    }
  };

  return { enqueueJob, isEnqueueing };
}
